// frontend/src/components/features/LetsPlayModal.jsx
// Shared Let's Play modal component for Collection, Explore, and Simplify pages

import React from "react";
import SessionForm from "../common/SessionForm";
import ScorePad from "./ScorePad";

/**
 * LetsPlayModal - Unified Let's Play modal for all pages
 *
 * Props:
 * - isOpen: boolean - Whether modal is visible
 * - game: Game|null - Selected game
 * - user: User|null - Current user
 * - onClose: () => void - Close modal callback
 * - showSessionForm: boolean - Show session form inline
 * - showRulesSummary: boolean - Show rules summary inline
 * - showScorePad: boolean - Show ScorePad inline
 * - onLogSession: () => void - Handler for Log Session button
 * - onSeeRulesSummary: () => void - Handler for Rules Summary button (optional)
 * - onOpenScorePad: () => void - Handler for ScorePad button
 * - onCloseScorePad: () => void - Close ScorePad callback
 * - onLogSessionFromScorePad: (data: object) => void - Handler when logging from ScorePad
 * - scorePadSessionData: object - Session data from ScorePad (participants with scores)
 * - sessionData: object - Session form data (for Simplify)
 * - setSessionData: (data: object) => void - Session data setter (for Simplify)
 * - onSaveSession: (result: object) => void - Save session callback
 * - showBanner: (msg: string, type?: string) => void - Show banner notification
 * - userMap: Map - User cache map
 * - fetchUserInfo: (userId: number) => Promise<string> - Fetch user info function
 * - source: 'simplify' | 'collection' | 'explore' - Source page for analytics/styling
 */

function LetsPlayModal({
  isOpen,
  game,
  user,
  onClose,
  showSessionForm = false,
  showRulesSummary = false,
  showScorePad = false,
  onLogSession,
  _onSeeRulesSummary,
  onOpenScorePad,
  onCloseScorePad,
  onLogSessionFromScorePad,
  scorePadSessionData,
  sessionData,
  setSessionData,
  onSaveSession,
  showBanner,
  userMap,
  fetchUserInfo,
  source = "collection",
}) {
  if (!isOpen || !game) return null;

  // Determine modal width based on content
  const isWideModal = showSessionForm || showScorePad;

  // Handle overlay click
  const handleOverlayClick = () => {
    if (onCloseScorePad) onCloseScorePad();
    onClose();
  };

  // Handle ScorePad log session
  const handleScorePadLogSession = (data) => {
    if (onLogSessionFromScorePad) {
      onLogSessionFromScorePad(data);
    }
  };

  // Render main content based on state
  const renderContent = () => {
    // ScorePad view
    if (showScorePad) {
      return (
        <ScorePad
          game={game}
          user={user}
          onClose={onCloseScorePad || onClose}
          onLogSession={handleScorePadLogSession}
          showBanner={showBanner || (() => {})}
        />
      );
    }

    // Session Form view
    if (showSessionForm) {
      // For Simplify with sessionData/setSessionData
      if (sessionData && setSessionData) {
        return (
          <SessionForm
            user={user}
            initialGame={sessionData?.game}
            initialSessionData={sessionData}
            onDataChange={setSessionData}
            onSave={onSaveSession}
            onCancel={() => {
              if (onCloseScorePad) onCloseScorePad();
              onClose();
            }}
            showBanner={showBanner}
            userMap={userMap}
            fetchUserInfo={fetchUserInfo}
          />
        );
      }

      // For Collection/Explore - use scorePadSessionData if available
      const initialData = scorePadSessionData || null;
      return (
        <SessionForm
          user={user}
          initialGame={game}
          initialSessionData={initialData}
          onSave={(result) => {
            if (onSaveSession) onSaveSession(result);
            onClose();
          }}
          onCancel={() => {
            if (onCloseScorePad) onCloseScorePad();
            onClose();
          }}
          showBanner={showBanner || (() => {})}
          userMap={userMap}
          fetchUserInfo={fetchUserInfo}
        />
      );
    }

    // Rules Summary view
    if (showRulesSummary) {
      return (
        <div>
          <h2
            style={{
              marginBottom: "1rem",
              color: "var(--text-primary, #333)",
            }}
          >
            Rules Summary for {game.name}
          </h2>
          <div
            style={{
              marginBottom: "2rem",
              color: "var(--text-secondary, #666)",
              textAlign: "left",
              lineHeight: "1.6",
            }}
          >
            <p style={{ marginBottom: "1rem" }}>
              Here&apos;s a quick refresher on how to play {game.name}:
            </p>
            <p>Rules summary functionality will be implemented here.</p>
            <ul style={{ textAlign: "left", marginLeft: "1.5rem" }}>
              <li>Setup instructions</li>
              <li>Game objective</li>
              <li>Turn structure</li>
              <li>Winning conditions</li>
              <li>Special rules or exceptions</li>
            </ul>
          </div>
          <div style={{ display: "flex", gap: "1rem", justifyContent: "center" }}>
            <button
              onClick={() => {
                if (onCloseScorePad) onCloseScorePad();
                onLogSession();
              }}
              style={{
                padding: "0.75rem 1.5rem",
                backgroundColor: "var(--pista-green-primary, #2E7D32)",
                color: "white",
                border: "none",
                borderRadius: "4px",
                cursor: "pointer",
                fontSize: "1rem",
                fontWeight: "bold",
              }}
            >
              Log Session
            </button>
            <button
              onClick={() => {
                if (onCloseScorePad) onCloseScorePad();
                onClose();
              }}
              style={{
                padding: "0.75rem 1.5rem",
                backgroundColor: "var(--bg-secondary, #f5f5f5)",
                color: "var(--text-primary, #333)",
                border: "1px solid var(--border-color, #ddd)",
                borderRadius: "4px",
                cursor: "pointer",
                fontSize: "1rem",
              }}
            >
              Back
            </button>
          </div>
        </div>
      );
    }

    // Main view with action buttons
    return (
      <div>
        <h2
          style={{
            marginBottom: "1rem",
            color: "var(--text-primary, #333)",
          }}
        >
          {game.name}
        </h2>
        <p
          style={{
            marginBottom: "2rem",
            color: "var(--text-secondary, #666)",
          }}
        >
          What would you like to do?
        </p>
        {game.thumbnail && (
          <img
            src={game.thumbnail}
            alt={game.name}
            style={{
              maxWidth: "200px",
              maxHeight: "200px",
              width: "auto",
              height: "auto",
              objectFit: "contain",
              borderRadius: "4px",
              marginBottom: "1rem",
              display: "block",
              marginLeft: "auto",
              marginRight: "auto",
            }}
          />
        )}
        <div style={{ display: "flex", gap: "1rem", justifyContent: "center" }}>
          <button
            onClick={onLogSession}
            style={{
              padding: "0.75rem 1.5rem",
              backgroundColor: "var(--success-color, #4caf50)",
              color: "var(--button-primary-text, white)",
              border: "none",
              borderRadius: "4px",
              cursor: "pointer",
              fontSize: "1rem",
              flex: 1,
              minWidth: "150px",
            }}
            onMouseOver={(e) =>
              (e.target.style.backgroundColor = "var(--success-color-dark, #45a049)")
            }
            onMouseOut={(e) =>
              (e.target.style.backgroundColor = "var(--success-color, #4caf50)")
            }
          >
            Add to Timeline
          </button>
          <button
            onClick={onOpenScorePad}
            style={{
              padding: "0.75rem 1.5rem",
              backgroundColor: "var(--primary-color, #2196f3)",
              color: "var(--button-primary-text, white)",
              border: "none",
              borderRadius: "4px",
              cursor: "pointer",
              fontSize: "1rem",
              flex: 1,
              minWidth: "150px",
            }}
            onMouseOver={(e) =>
              (e.target.style.backgroundColor = "var(--primary-color-dark, #1976d2)")
            }
            onMouseOut={(e) =>
              (e.target.style.backgroundColor = "var(--primary-color, #2196f3)")
            }
          >
            Score Pad
          </button>
        </div>
        <button
          onClick={handleOverlayClick}
          style={{
            marginTop: "1rem",
            padding: "0.5rem 1rem",
            backgroundColor: "var(--bg-secondary, #f5f5f5)",
            color: "var(--text-secondary, #666)",
            border: "1px solid var(--border-color, #ddd)",
            borderRadius: "4px",
            cursor: "pointer",
          }}
        >
          Cancel
        </button>
      </div>
    );
  };

  return (
    <div
      className="safe-area-overlay"
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        backgroundColor: "rgba(0,0,0,0.5)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1000,
      }}
      onClick={handleOverlayClick}
      data-testid="lets-play-modal-overlay"
      data-source={source}
    >
      <div
        style={{
          backgroundColor: "var(--bg-primary, white)",
          padding: "2rem",
          borderRadius: "8px",
          maxWidth: isWideModal ? "800px" : "400px",
          width: "90%",
          maxHeight: "80dvh",
          overflow: "auto",
          textAlign: "center",
        }}
        onClick={(e) => e.stopPropagation()}
        data-testid="lets-play-modal-content"
      >
        {renderContent()}
      </div>
    </div>
  );
}

export default LetsPlayModal;
